/*
 * Copyright (c) RECREWT GmbH 2022.
 */

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { fromEvent, Observable, of, single, Subject } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import { ENTER } from '@angular/cdk/keycodes';
import {
  AbstractControl,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipGrid } from '@angular/material/chips';
import { CustomFormControlComponent } from '@shared/components/editor/custom-form-control/custom-form-control.component';
import { TranslateService } from '@ngx-translate/core';
import { get } from 'lodash';

@Component({
  selector: 'recrewt-chip-editor',
  templateUrl: './chip-editor.component.html',
  styleUrls: ['./chip-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ChipEditorComponent,
    },
  ],
  standalone: false,
})
export class ChipEditorComponent extends CustomFormControlComponent implements OnInit, OnChanges {
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  readonly separatorKeysCodes: number[] = [ENTER];

  readonly searchCtrl = new UntypedFormControl();

  fromCustom: string[] = [];

  fromPredefined: string[] = [];

  filteredPredefined: Observable<string[]> = of([]);

  editable = false;

  dirty = false;

  @ViewChild('chipList') chipList?: MatChipGrid;

  @ViewChild('valueInput') valueInput?: ElementRef<HTMLInputElement>;

  @Output() update = new EventEmitter<{
    predefined: string[];
    custom: string[];
  }>();

  @Output() chipClick = new EventEmitter<{
    predefined: boolean;
    value: string;
    i: number;
  }>();

  @Output() edit = new EventEmitter<void>();

  @Output() chipAdd = new EventEmitter<{ predefined: boolean; i: number }>();

  @Output() chipRemove = new EventEmitter<{
    predefined: boolean;
    i: number;
  }>();

  @Input() labelKey: string | null = null;

  @Input() maxLength = 100;

  @Input() deletableInViewMode = false;

  @Input() predefinedOnly = false;

  @Input() disableDeleteForInitialSelection = false;

  @Input() selectedCustom: string[] = [];

  @Input() selectedPredefined: any[] = [];

  @Input() predefined: any[] = [];

  @Input() disabledOptionSuffix: string = 'COMMON.disabled';

  @Input() disabledOptions: any[] = [];

  @Input() optionValueKey?: string;

  @Input() optionTpl?: TemplateRef<any>;

  private closed = new Subject<void>();

  indexOf = (option: string) => {
    return this.predefined.indexOf(option);
  };

  ngOnInit(): void {
    this.fromCustom = Object.assign([], this.selectedCustom);
    this.setAutoCompleteOptions();
  }

  add(event: MatChipInputEvent): void {
    const input = event.chipInput?.inputElement;
    if (!input) {
      return;
    }
    const value = event.value.trim();
    this.doAdd(value, input);
    this.update.emit(this.getValue());
  }

  remove(value: string): void {
    const index = this.fromCustom?.indexOf(value);
    if (index >= 0) {
      this.fromCustom?.splice(index, 1);
      this.notifyChange();
      this.chipRemove.emit({
        predefined: false,
        i: this.fromPredefined.length + index,
      });
    }
    const indexPredefined = this.fromPredefined?.indexOf(value);
    if (indexPredefined >= 0) {
      this.fromPredefined?.splice(indexPredefined, 1);
      this.notifyChange();
      this.chipRemove.emit({ predefined: true, i: indexPredefined });
    }
    this.dirty = true;
    this.searchCtrl.setValue(this.searchCtrl.value);
    this.update.emit(this.getValue());
  }

  writeValue(values: { predefined: string[]; custom: string[] }): void {
    if (!values) {
      return;
    }
    this.fromPredefined = values.predefined ?? [];
    this.setAutoCompleteOptions();
    this.fromCustom = this.predefinedOnly ? [] : (values.custom ?? []);
  }

  close(): void {
    const input = this.valueInput?.nativeElement;
    if (input?.value) {
      this.doAdd(input.value, input);
      this.dirty = true;
    }
    this.editable = false;
    if (this.dirty) {
      this.update.emit(this.getValue());
      this.dirty = false;
    }
    this.closed.next();
    this.cdr.detectChanges();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.fromPredefined?.push(event.option.value);
    this.notifyChange();
    this.chipAdd.emit({
      predefined: true,
      i: this.fromPredefined.length - 1,
    });
    this.update.emit(this.getValue());

    const input = this.valueInput?.nativeElement;
    if (!input) {
      return;
    }
    input.value = '';
    this.searchCtrl.setValue(null);
    this.dirty = true;
  }

  open(event: MouseEvent) {
    if (!this.editable) {
      event.stopPropagation();
    }
    this.editable = true;
    // The input is only unhidden by the next change detection run, and focus() is a no-op while
    // it is still display:none — so defer it by a tick.
    setTimeout(() => this.valueInput?.nativeElement.focus(), 0);
    this.handleClickOutside();
    this.edit.emit();
  }

  isDisabled(option: string, disabledOptions: string[]): boolean {
    return disabledOptions.includes(option);
  }

  isDeleteDisabled = (option: string) => {
    const preselectedValues = this.selectedPredefined.map((o) => this.optionValue(o));
    return this.disableDeleteForInitialSelection && preselectedValues.includes(option);
  };

  ngOnChanges(changes: SimpleChanges): void {
    // `predefined` usually arrives from an async pipe, i.e. after the autocomplete already
    // subscribed to `filteredPredefined`. That stream only re-runs the filter on searchCtrl
    // changes, so without re-creating it the panel stays empty until the user types.
    if (changes['predefined']) {
      this.setAutoCompleteOptions();
    }
    if (changes['autocompleteOptions']?.currentValue) {
      this.predefined = this.predefined.map((o) => this.optionValue(o));
    }
    if (changes['disabledOptions']?.currentValue) {
      this.disabledOptions = this.disabledOptions.map((o) => this.optionValue(o));
    }
    if (changes['selectedPredefined']?.currentValue) {
      this.fromPredefined = this.selectedPredefined.map((o) => this.optionValue(o));
    }
    if (changes['autocompleteOptions']?.currentValue || changes['disabledOptions']?.currentValue) {
      this.fromPredefined = this.fromPredefined.filter(
        (it) => this.predefined.includes(it) && !this.disabledOptions.includes(it),
      );
      this.setAutoCompleteOptions();
      this.update.emit(this.getValue());
    }
  }

  protected optionValue(o: any) {
    return this.optionValueKey ? get(o, this.optionValueKey) : o;
  }

  private optionName(o: any) {
    return this.labelKey ? get(o, this.labelKey) : o;
  }

  private setAutoCompleteOptions() {
    this.filteredPredefined = this.searchCtrl.valueChanges.pipe(
      startWith(''),
      map((value: string | null) => this._filter(value ?? '')),
    );
    const valueLabels = this.fromPredefined?.map((val) => this.optionName(val));
    this.filteredPredefined?.pipe(
      single(),
      map((values) => values.filter((value) => !valueLabels?.includes(this.optionName(value)))),
    );
  }

  private getValue(): any {
    return {
      custom: Object.assign([], this.fromCustom),
      predefined: Object.assign([], this.fromPredefined),
    };
  }

  private doAdd(value: string, input: HTMLInputElement): void {
    if (this.fromPredefined?.includes(value) || this.fromCustom?.includes(value)) {
      return;
    }
    if ((value || '').trim()) {
      if (this.predefined.includes(value)) {
        this.fromPredefined?.push(value);
        this.notifyChange();
        this.chipAdd.emit({
          predefined: true,
          i: this.fromPredefined.length - 1,
        });
      } else if (!this.predefinedOnly) {
        this.fromCustom?.push(value);
        this.notifyChange();
        this.chipAdd.emit({
          predefined: false,
          i: this.fromPredefined.length + this.fromCustom.length - 1,
        });
      }
      this.dirty = true;
    }
    if (input) {
      input.value = '';
    }
    this.searchCtrl.setValue(null);
    setTimeout(() => this.valueInput?.nativeElement.focus(), 0); // workaround for autocomplete to stay visible
  }

  private notifyChange(): void {
    this.onChanged(this.getValue());
  }

  private _filter(value: any): string[] {
    const valueLabels = this.fromPredefined?.map((val) => this.optionName(val));
    if (typeof value !== 'string') {
      return this.predefined.filter((item) => !valueLabels?.includes(this.optionName(item)));
    }
    const prefiltered = this.predefined.filter(
      (item) => !valueLabels?.includes(this.optionName(item)),
    );
    if (!value) {
      return prefiltered;
    }
    const filterValue = value?.toLowerCase();
    return prefiltered.filter(
      (val) =>
        this.translate.instant(this.optionName(val)).toLowerCase().indexOf(filterValue) !== -1,
    );
  }

  private handleClickOutside(): void {
    fromEvent(document, 'click')
      .pipe(
        takeUntil(this.closed),
        filter(({ target }) => {
          const inputElement = this.valueInput?.nativeElement as HTMLElement;
          const el = target as Element | null;
          // `closest` instead of a className check: with MDC the click lands on the option's inner
          // `.mdc-list-item__primary-text` span, and `className` is not a string on SVG targets.
          return (
            !inputElement?.contains(el as HTMLElement) &&
            !el?.closest?.('mat-option, .mat-mdc-autocomplete-panel')
          );
        }),
      )
      .subscribe(() => this.close());
  }
}

export namespace ChipEditor {
  export function required(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const selection = control.value;
      return !!selection?.custom?.length || selection?.predefined?.length
        ? null
        : { required: true };
    };
  }

  export function requiredIf(condition: boolean): ValidatorFn | null {
    return condition ? ChipEditor.required() : Validators.nullValidator;
  }
}
